import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/axiosInstance";

// Fetch Orders
export const fetchOrders = createAsyncThunk(
  "orders/fetchAll",
  async (query = "", thunkAPI) => {
    try {
      const res = await api.get(`/dashboard/orders${query}`);
      return res.data.data.orders;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get Order by ID
export const getOrder = createAsyncThunk(
  "orders/get",
  async (orderId, thunkAPI) => {
    try {
      const res = await api.get(`/dashboard/orders/${orderId}`);
      return res.data.data.order;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Order
export const updateOrder = createAsyncThunk(
  "orders/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await api.put(`/dashboard/orders/${id}`, data);
      return res.data.data.order;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Refund Order
export const refundOrder = createAsyncThunk(
  "orders/refund",
  async ({ orderId, data }, thunkAPI) => {
    try {
      const res = await api.put(`/dashboard/orders/${orderId}/refund-order`, data);
      return res.data.data.order;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Export Orders
export const exportOrders = createAsyncThunk(
  "orders/export",
  async ({ ids, fields }, thunkAPI) => {
    try {
      const res = await api.post(`/dashboard/export-orders`, { ids, fields }, {
        responseType: 'blob',
      });
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const ordersSlicer = createSlice({
  name: "orders",
  initialState: {
    data: [],
    currentOrder: null,
    loading: {
      fetch: false,
      get: false,
      update: false,
      refund: false,
      export: false,
    },
    error: null,
    apiErrors: {},
    successMessage: "",
  },
  reducers: {
    clearOrdersState(state) {
      state.data = [];
      state.currentOrder = null;
      state.loading = { fetch: false, get: false, update: false, refund: false, export: false };
      state.error = null;
      state.apiErrors = {};
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading.fetch = true;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.data = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = true;
      })

      .addCase(getOrder.pending, (state) => {
        state.loading.get = true;
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        state.loading.get = false;
        state.currentOrder = action.payload;
      })
      .addCase(getOrder.rejected, (state) => {
        state.loading.get = false;
        state.error = true;
      })

      .addCase(updateOrder.pending, (state) => {
        state.loading.update = true;
        state.apiErrors = {};
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.loading.update = false;
        // Update order in list if data structure exists
        if (state.data && Array.isArray(state.data.data) && state.data.data.length > 0) {
          const index = state.data.data.findIndex(o => o.id === action.payload.id);
          if (index !== -1) state.data.data[index] = action.payload;
        }
        // Update currentOrder if it matches
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
        state.successMessage = action.payload?.message || "";
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.loading.update = false;
        state.apiErrors = action.payload?.errors || {};
      })

      .addCase(refundOrder.pending, (state) => {
        state.loading.refund = true;
        state.apiErrors = {};
      })
      .addCase(refundOrder.fulfilled, (state, action) => {
        state.loading.refund = false;
        // Update order in list if data structure exists
        if (state.data && Array.isArray(state.data.data) && state.data.data.length > 0) {
          const index = state.data.data.findIndex(o => o.id == action.payload.id);
          if (index !== -1) state.data.data[index] = action.payload;
        }
        // Update currentOrder if it matches
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })

      .addCase(refundOrder.rejected, (state, action) => {
        state.loading.refund = false;
        state.apiErrors = action.payload?.errors || {};
      })

      .addCase(exportOrders.pending, (state) => {
        state.loading.export = true;
      })
      .addCase(exportOrders.fulfilled, (state) => {
        state.loading.export = false;
      })
      .addCase(exportOrders.rejected, (state) => {
        state.loading.export = false;
        state.error = true;
      });
  }
});

export const { clearOrdersState } = ordersSlicer.actions;
export default ordersSlicer.reducer;
